import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../config/api';
import { formatCurrency } from '../../utils/dateUtils';
import { getUserData } from '../../utils/auth';
import { CLOVER_ALLOWED_USERS } from '../../utils/userUtils';
import './WigRepairBalanceReport.css';

const STORAGE_KEY = 'wigRepairBalanceReport:v5';

const WARRANTY_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'under', label: 'Under warranty' },
  { key: 'expired', label: 'No warranty' },
];

// Known locations, shown first and in this order; anything else in the data
// (e.g. "China", blanks) is appended.
const KNOWN_LOCATIONS = ['Pomona', 'Lakewood', 'Israel'];

// Buckets a row for the warranty badge / filter.
const warrantyState = (r) => {
  if (r.underWarranty) return 'under';
  if (r.warrantyExpires) return 'expired';
  return 'none';
};

// Local date, not UTC: toISOString() rolls over to tomorrow in the evening.
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const yearStartISO = () => `${new Date().getFullYear()}-01-01`;

// Show dates the way the front desk reads them (M/D/YYYY). Tolerant of both
// ISO (yyyy-mm-dd, what the API sends) and already-formatted M/D/YYYY values.
const fmtDate = (value) => {
  if (!value) return '—';
  const s = String(value).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${Number(iso[2])}/${Number(iso[3])}/${iso[1]}`;
  if (s.includes('/')) return s.split(/[ T]/)[0]; // already M/D/YYYY
  return s;
};

// Sortable key: yyyymmdd number, 0 when the date can't be parsed.
const dateSortKey = (value) => {
  if (!value) return 0;
  const s = String(value).trim();
  let y;
  let m;
  let d;
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    [, y, m, d] = iso;
  } else {
    const us = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (!us) return 0;
    [, m, d, y] = us;
  }
  return Number(y) * 10000 + Number(m) * 100 + Number(d);
};

const fmtSavedAt = (iso) =>
  new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });

const loadSaved = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const TEXT_FIELDS = new Set(['wigCode', 'clientName', 'enteredBy']);

const WigRepairBalanceReport = ({ user }) => {
  const currentUser = user || getUserData();
  const username = (currentUser?.UserName || currentUser?.username || '').toLowerCase();
  const isAllowed = CLOVER_ALLOWED_USERS.includes(username);

  const saved = useMemo(() => loadSaved(), []);

  const [startDate, setStartDate] = useState(saved?.startDate || yearStartISO());
  // Always reopen with "To" = today. Restoring the saved end date froze the
  // pickers at the last run's date ("From" is capped at "To"). The saved
  // report's own range still shows via meta.
  const [endDate, setEndDate] = useState(todayISO());
  const [onlyOutstanding, setOnlyOutstanding] = useState(saved?.onlyOutstanding ?? false);
  const [warrantyFilter, setWarrantyFilter] = useState(saved?.warrantyFilter || 'all');
  const [locationFilter, setLocationFilter] = useState(saved?.locationFilter || 'all');
  const [search, setSearch] = useState(saved?.search || '');
  const [sort, setSort] = useState(saved?.sort || { field: 'orderDate', dir: 'desc' });

  const [rows, setRows] = useState(saved?.rows || []);
  const [meta, setMeta] = useState(saved?.meta || null);
  const [savedAt, setSavedAt] = useState(saved?.savedAt || null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Persist the whole report so it survives a refresh until she clears it.
  useEffect(() => {
    if (!savedAt) return;
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          startDate, endDate, onlyOutstanding, warrantyFilter, locationFilter,
          search, sort, rows, meta, savedAt,
        })
      );
    } catch {
      /* storage disabled or full */
    }
  }, [startDate, endDate, onlyOutstanding, warrantyFilter, locationFilter, search, sort, rows, meta, savedAt]);

  const generate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/api/reports/wig-repair-balances`, {
        params: { startDate, endDate },
      });
      const data = res.data?.data || [];
      setRows(data);
      setMeta({
        count: res.data?.count ?? data.length,
        outstandingCount: res.data?.outstandingCount ?? 0,
        underWarrantyCount:
          res.data?.underWarrantyCount ?? data.filter((r) => r.underWarranty).length,
        totalOutstanding: res.data?.totalOutstanding ?? 0,
        startDate: res.data?.startDate || startDate,
        endDate: res.data?.endDate || endDate,
      });
      setSavedAt(new Date().toISOString());
    } catch (err) {
      console.error('Failed to load wig repair balance report:', err);
      setError(err.response?.data?.message || err.message || 'Could not load the report. Try again.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const clearSaved = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setRows([]);
    setMeta(null);
    setSavedAt(null);
    setSearch('');
    setOnlyOutstanding(false);
    setWarrantyFilter('all');
    setLocationFilter('all');
    setSort({ field: 'orderDate', dir: 'desc' });
    setStartDate(yearStartISO());
    setEndDate(todayISO());
    setError(null);
  };

  const availableLocations = useMemo(() => {
    const seen = new Set(rows.map((r) => (r.location || '').trim()).filter(Boolean));
    const extras = [...seen].filter((l) => !KNOWN_LOCATIONS.includes(l)).sort();
    return [...KNOWN_LOCATIONS.filter((l) => seen.has(l)), ...extras];
  }, [rows]);

  const toggleSort = (field) => {
    setSort((prev) =>
      prev.field === field
        ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { field, dir: TEXT_FIELDS.has(field) ? 'asc' : 'desc' }
    );
  };

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      if (onlyOutstanding && r.balance <= 0.01) return false;
      if (warrantyFilter === 'under' && !r.underWarranty) return false;
      if (warrantyFilter === 'expired' && r.underWarranty) return false;
      if (locationFilter !== 'all' && (r.location || '').trim() !== locationFilter) return false;
      if (!q) return true;
      return (
        String(r.wigCode).toLowerCase().includes(q) ||
        String(r.clientName).toLowerCase().includes(q) ||
        String(r.enteredBy).toLowerCase().includes(q) ||
        String(r.orderNo).includes(q)
      );
    });

    const { field, dir } = sort;
    const mult = dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      if (field === 'orderDate') {
        const diff = dateSortKey(a.orderDate) - dateSortKey(b.orderDate);
        return (diff || a.orderNo - b.orderNo) * mult;
      }
      if (TEXT_FIELDS.has(field)) {
        const av = String(a[field] || '').toLowerCase();
        const bv = String(b[field] || '').toLowerCase();
        if (av < bv) return -1 * mult;
        if (av > bv) return 1 * mult;
        return (a.orderNo - b.orderNo) * mult;
      }
      return ((a[field] ?? 0) - (b[field] ?? 0)) * mult;
    });
  }, [rows, search, onlyOutstanding, warrantyFilter, locationFilter, sort]);

  const visibleOutstanding = useMemo(
    () => visibleRows.reduce((sum, r) => sum + (r.balance > 0 ? r.balance : 0), 0),
    [visibleRows]
  );

  const exportCsv = () => {
    const header = [
      'Wig Code', 'Client Name', 'Order #', 'Order Date', 'Entered By', 'Location',
      'Warranty', 'Warranty Expires', 'Order Total', 'Paid', 'Balance', 'Status',
    ];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const warrantyLabel = { under: 'Under warranty', expired: 'Warranty expired', none: 'No warranty' };
    const lines = [header.join(',')];
    visibleRows.forEach((r) => {
      lines.push([
        esc(r.wigCode),
        esc(r.clientName),
        esc(r.orderNo),
        esc(fmtDate(r.orderDate)),
        esc(r.enteredBy),
        esc(r.location),
        esc(warrantyLabel[warrantyState(r)]),
        esc(r.warrantyExpires ? fmtDate(r.warrantyExpires) : ''),
        r.orderTotal.toFixed(2),
        r.totalPaid.toFixed(2),
        r.balance.toFixed(2),
        esc(r.balance > 0.01 ? 'Balance due' : 'Paid in full'),
      ].join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wig-repair-balances_${meta?.startDate || startDate}_to_${meta?.endDate || endDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const warrantyBadge = (r) => {
    const state = warrantyState(r);
    if (state === 'under') {
      return (
        <span
          className="wrbr-warranty wrbr-warranty-under"
          title={r.warrantyExpires ? `Warranty runs through ${fmtDate(r.warrantyExpires)}` : 'Wig is under warranty'}
        >
          <i className="fas fa-shield-halved" aria-hidden="true" />
          Under warranty
        </span>
      );
    }
    if (state === 'expired') {
      return (
        <span className="wrbr-warranty wrbr-warranty-expired" title={`Warranty expired ${fmtDate(r.warrantyExpires)}`}>
          Expired {fmtDate(r.warrantyExpires)}
        </span>
      );
    }
    return <span className="wrbr-warranty wrbr-warranty-none">No warranty</span>;
  };

  // Render helper (not a nested component) so the header button keeps keyboard
  // focus across re-sorts instead of remounting.
  const sortTh = (field, label, numeric = false) => {
    const active = sort.field === field;
    return (
      <th
        className={`wrbr-th-sort${numeric ? ' wrbr-num' : ''}${active ? ' is-active' : ''}`}
        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <button type="button" onClick={() => toggleSort(field)}>
          {label}
          <i
            className={`fas ${active ? (sort.dir === 'asc' ? 'fa-caret-up' : 'fa-caret-down') : 'fa-sort'}`}
            aria-hidden="true"
          />
        </button>
      </th>
    );
  };

  if (!isAllowed) {
    return (
      <div className="wrbr-page">
        <div className="wrbr-empty wrbr-denied">
          <i className="fas fa-lock" aria-hidden="true" />
          <p>You don't have access to the Wig Repair Balances report.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wrbr-page" aria-busy={loading}>
      <header className="wrbr-header">
        <h1>
          <i className="fas fa-scale-balanced" aria-hidden="true" />
          Wig Repair Balances
        </h1>
        <p className="wrbr-subtitle">
          Every wig repair in the selected range, with what each client still owes.
        </p>
      </header>

      <form
        className="wrbr-filters"
        onSubmit={(e) => {
          e.preventDefault();
          generate();
        }}
      >
        <label className="wrbr-field">
          <span>From</span>
          <input type="date" value={startDate} max={endDate} onChange={(e) => setStartDate(e.target.value)} />
        </label>
        <label className="wrbr-field">
          <span>To</span>
          <input
            type="date"
            value={endDate}
            min={startDate}
            max={todayISO()}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </label>
        <button type="submit" className="wrbr-btn wrbr-btn-primary" disabled={loading}>
          <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true" />
          {loading ? 'Loading' : meta ? 'Refresh report' : 'Generate report'}
        </button>
      </form>

      {error && (
        <div className="wrbr-error" role="alert">
          <i className="fas fa-triangle-exclamation" aria-hidden="true" />
          {error}
        </div>
      )}

      {meta && (
        <>
          <div className="wrbr-results-head">
            <div className="wrbr-range">
              <span className="wrbr-range-dates">
                {fmtDate(meta.startDate)} &nbsp;to&nbsp; {fmtDate(meta.endDate)}
              </span>
              {savedAt && (
                <span className="wrbr-saved" title="Saved in this browser until you clear it">
                  <i className="fas fa-clock-rotate-left" aria-hidden="true" /> Saved {fmtSavedAt(savedAt)}
                </span>
              )}
            </div>
            <div className="wrbr-results-actions">
              <button type="button" className="wrbr-btn" onClick={exportCsv} disabled={!visibleRows.length}>
                <i className="fas fa-file-csv" aria-hidden="true" /> Export CSV
              </button>
              <button type="button" className="wrbr-btn wrbr-btn-ghost" onClick={clearSaved}>
                <i className="fas fa-trash-can" aria-hidden="true" /> Clear saved report
              </button>
            </div>
          </div>

          <div className="wrbr-stats">
            <div className="wrbr-stat">
              <span className="wrbr-stat-num">{meta.count}</span>
              <span className="wrbr-stat-label">Repairs in range</span>
            </div>
            <div className="wrbr-stat">
              <span className="wrbr-stat-num">{meta.outstandingCount}</span>
              <span className="wrbr-stat-label">With a balance</span>
            </div>
            <div className="wrbr-stat">
              <span className="wrbr-stat-num">{meta.underWarrantyCount ?? 0}</span>
              <span className="wrbr-stat-label">Under warranty</span>
            </div>
            <div className={`wrbr-stat ${meta.totalOutstanding > 0 ? 'wrbr-stat-danger' : 'wrbr-stat-clear'}`}>
              <span className="wrbr-stat-num">{formatCurrency(meta.totalOutstanding)}</span>
              <span className="wrbr-stat-label">Total outstanding</span>
            </div>
          </div>

          <div className="wrbr-toolbar">
            <input
              type="search"
              className="wrbr-search"
              aria-label="Filter repairs by client, wig code, or order number"
              placeholder="Filter by client, wig code, or order number"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {availableLocations.length > 1 && (
              <label className="wrbr-select-field">
                <span className="wrbr-sr-only">Filter by location</span>
                <select
                  className="wrbr-select"
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                >
                  <option value="all">All locations</option>
                  {availableLocations.map((loc) => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>
              </label>
            )}
            <div className="wrbr-segmented" role="group" aria-label="Filter by warranty">
              {WARRANTY_FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  className={warrantyFilter === f.key ? 'is-active' : ''}
                  aria-pressed={warrantyFilter === f.key}
                  onClick={() => setWarrantyFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <label className="wrbr-check">
              <input
                type="checkbox"
                checked={onlyOutstanding}
                onChange={(e) => setOnlyOutstanding(e.target.checked)}
              />
              Only show balances owed
            </label>
          </div>

          <div className="wrbr-table-wrap">
            <table className="wrbr-table" aria-label="Wig repair balances">
              <thead>
                <tr>
                  {sortTh('wigCode', 'Wig Code')}
                  {sortTh('clientName', 'Client')}
                  <th>Order #</th>
                  {sortTh('orderDate', 'Order Date')}
                  <th>Entered By</th>
                  <th>Location</th>
                  <th>Warranty</th>
                  {sortTh('orderTotal', 'Total', true)}
                  {sortTh('totalPaid', 'Paid', true)}
                  {sortTh('balance', 'Balance', true)}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r) => {
                  const owes = r.balance > 0.01;
                  const credit = r.balance < -0.01;
                  return (
                    <tr key={r.orderNo} className={owes ? 'wrbr-row-owes' : ''}>
                      <td className="wrbr-mono">{r.wigCode || '—'}</td>
                      <td>
                        {r.customerId ? (
                          <Link
                            to={`/customers/${r.customerId}`}
                            className="wrbr-link"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Open ${r.clientName || `customer #${r.customerId}`}'s profile in a new tab`}
                          >
                            {r.clientName || `Customer #${r.customerId}`}
                          </Link>
                        ) : (
                          r.clientName || '—'
                        )}
                      </td>
                      <td className="wrbr-mono">
                        <Link
                          to={`/work-order-profile/${r.orderNo}`}
                          className="wrbr-link"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Open work order ${r.orderNo} in a new tab`}
                        >
                          {r.orderNo}
                        </Link>
                      </td>
                      <td>{fmtDate(r.orderDate)}</td>
                      <td>{r.enteredBy || '—'}</td>
                      <td>{r.location || '—'}</td>
                      <td>{warrantyBadge(r)}</td>
                      <td className="wrbr-num">{formatCurrency(r.orderTotal)}</td>
                      <td className="wrbr-num">{formatCurrency(r.totalPaid)}</td>
                      <td className="wrbr-num">
                        {credit ? (
                          <span className="wrbr-bal wrbr-bal-credit">
                            {formatCurrency(Math.abs(r.balance))} credit
                          </span>
                        ) : owes ? (
                          <span className="wrbr-bal wrbr-bal-owes">{formatCurrency(r.balance)}</span>
                        ) : (
                          <span className="wrbr-bal wrbr-bal-paid">Paid</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!visibleRows.length && (
                  <tr>
                    <td colSpan={10} className="wrbr-table-empty">
                      {rows.length
                        ? 'No repairs match the current filters.'
                        : 'No wig repairs found in this date range.'}
                    </td>
                  </tr>
                )}
              </tbody>
              {visibleRows.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={9} className="wrbr-num">
                      Outstanding, filtered view
                    </td>
                    <td className="wrbr-num">
                      <strong>{formatCurrency(visibleOutstanding)}</strong>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}

      {!meta && loading && (
        <div className="wrbr-empty" role="status">
          <i className="fas fa-spinner fa-spin" aria-hidden="true" />
          <p>Pulling wig repairs&hellip;</p>
        </div>
      )}

      {!meta && !loading && !error && (
        <div className="wrbr-empty">
          <i className="fas fa-clipboard-list" aria-hidden="true" />
          <p>Pick a date range, then select <strong>Generate report</strong>.</p>
          <p className="wrbr-empty-hint">Your report stays saved in this browser until you clear it.</p>
        </div>
      )}
    </div>
  );
};

export default WigRepairBalanceReport;
